import { getItemFromStorage, setItemToStorage } from '@utils/storage';
import { logError } from '@utils/usefulFuncs';

// The last few reads an analyst actually ran, so a query worth repeating doesn't have to be rebuilt.
// With a feed list on the page the retyping this saves is mostly the *window and company filter*,
// which is why a run records those rather than just the feed id.
//
// Persisted as JSON under one key. `getItemFromStorage` returns the raw string rather than parsing
// it, so parsing is the caller's job (as at src/pages/AgentSpreadsheet.js:123) — done defensively
// here, since a half-written or hand-edited value must not take the page down.
//
// Named runHistory rather than recentRuns because RecentRuns.js is the component beside it, and on a
// case-insensitive filesystem those two names are one file.

export const MAX_RECENT_RUNS = 10;
export const RECENT_RUNS_STORAGE_KEY = 'feedViewRecentRuns';

const toIso = (date) => {
  if (!date) return null;
  const time = new Date(date).getTime();
  return isNaN(time) ? null : new Date(time).toISOString();
};

// The params a run was made with, plus what came back — the row count is what makes a listed run
// worth recognising rather than just re-reading.
//
// The count is the server's own `row_count`, falling back to the rows in hand: the two agree by
// contract, and preferring the server's number means the record says what the read returned rather
// than what this client happened to keep. Nothing records truncation, because the server never
// truncates — over the cap is a 422, so a recorded run is a complete one by construction.
export const toRecentRun = ({ params, result, ranAt }) => ({
  feedId: params.feedId,
  knowableFrom: toIso(params.knowableFrom),
  knowableTo: toIso(params.knowableTo),
  companies: (params.companies || []).map((company) => ({
    company_id: company.company_id,
    symbol: company.symbol || null,
    name: company.name || null,
  })),
  slim: !!params.slim,
  rowCount: result?.rowCount ?? result?.rows?.length ?? null,
  ranAt: toIso(ranAt) || null,
});

// Identity is the query, not the moment: re-running the same read moves it up the list instead of
// filling the list with copies of itself.
export const runKey = (run) =>
  JSON.stringify([
    run.feedId,
    run.knowableFrom || null,
    run.knowableTo || null,
    (run.companies || [])
      .map((company) => company.company_id)
      .slice()
      .sort(),
    !!run.slim,
  ]);

export const addRecentRun = (runs, run) => {
  const key = runKey(run);
  return [run, ...(runs || []).filter((existing) => runKey(existing) !== key)].slice(0, MAX_RECENT_RUNS);
};

// Removal keys on the query, exactly as adding does — addRecentRun dedupes by the same key, so no
// two listed runs can share one and there is no ambiguity about which row goes.
export const removeRecentRun = (runs, run) => {
  const key = runKey(run);
  return (runs || []).filter((existing) => runKey(existing) !== key);
};

const isUsableRun = (run) => !!run && typeof run === 'object' && typeof run.feedId === 'string' && !!run.feedId;

export const loadRecentRuns = () => {
  try {
    const raw = getItemFromStorage(RECENT_RUNS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isUsableRun).slice(0, MAX_RECENT_RUNS);
  } catch (e) {
    // A corrupt value is worth losing, not worth crashing the page for.
    logError('loadRecentRuns', e);
    return [];
  }
};

export const saveRecentRuns = (runs) => {
  try {
    setItemToStorage(RECENT_RUNS_STORAGE_KEY, runs || []);
  } catch (e) {
    // Private browsing and a full quota both land here; the list is a convenience, not the feature.
    logError('saveRecentRuns', e);
  }
};

// Restoring a run only sets the fields it recorded. `useMock` is deliberately absent from a run and
// forced off here: a recorded run was a real read, and repeating it must not become a sample.
export const toFormPatch = (run) => ({
  feedId: run.feedId,
  fromDate: run.knowableFrom ? new Date(run.knowableFrom) : null,
  toDate: run.knowableTo ? new Date(run.knowableTo) : null,
  companies: run.companies || [],
  slim: !!run.slim,
  useMock: false,
});
